import {
  hasAnyPermission as hasAnyPermissionIn,
  hasPopulatedPermissionMap,
} from '../../assets/utils/permissions';

export const PLAN_REQUEST_STATUSES = [
  { label: 'Open', value: 'open' },
  { label: 'Pending', value: 'pending' },
  { label: 'Countered', value: 'countered' },
  { label: 'Org accepted', value: 'org_accepted' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'Cancelled', value: 'cancelled' },
];

/** Status filters for removal tickets (no countered / org_accepted). */
export const PLAN_REMOVAL_STATUSES = [
  { label: 'Open', value: 'open' },
  { label: 'Pending', value: 'pending' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'Cancelled', value: 'cancelled' },
];

export const PLAN_REQUEST_BOARD_TABS = [
  { label: 'Negotiations', value: 'add' },
  { label: 'Removals', value: 'remove' },
];

export const PLAN_REQUEST_OPEN_STATUSES = new Set(['pending', 'countered', 'org_accepted']);

export const PLAN_REQUEST_PERMISSIONS = [
  'admin.*',
  'corporations.manage',
  'organizations.manage',
];

export const LEGACY_PLAN_REQUEST_ROLES = [1, 5];

/**
 * Permission-first plan-request gate.
 *
 * `permissionSource` is either the bound `hasAnyPermission` from usePermissions() or the
 * raw permission map — both call styles work, so callers can migrate one at a time. The
 * legacy staff role ids are honoured ONLY while the session carries no RBAC permission
 * map at all; a populated map can never be overridden by a stale role id.
 */
export function canManagePlanRequests(user, permissionSource) {
  const granted = typeof permissionSource === 'function'
    ? Boolean(permissionSource(PLAN_REQUEST_PERMISSIONS))
    : hasAnyPermissionIn(PLAN_REQUEST_PERMISSIONS, permissionSource);
  if (granted) return true;
  const permissionMap = typeof permissionSource === 'function' ? null : permissionSource;
  if (hasPopulatedPermissionMap(permissionMap, user)) return false;
  return LEGACY_PLAN_REQUEST_ROLES.includes(Number(user?.role?.staffRoleId));
}

/** Same RBAC gate as plan requests (guide §3). */
export const canManageStaffUploads = canManagePlanRequests;

/**
 * Assigning the plans an organisation offers — the Manage plans panel.
 *
 * This used to reuse the plan-request gate, which conflates two different jobs:
 * approving an HR plan request is not the same as deciding which plans an
 * organisation carries. `corporations.manage_policies` is what the API has
 * always checked on the write path; it simply was not offered in the admin
 * permission list, so there was no way to grant this on its own.
 *
 * The coarse permissions stay accepted so nobody who could do this yesterday
 * loses it before the backfill migration runs.
 */
export const ORG_MANAGE_PLANS_PERMISSIONS = [
  'admin.*',
  'corporations.manage_policies',
  'corporations.manage',
  'organizations.manage',
];

export function canManageOrgPlans(user, permissionSource) {
  const granted = typeof permissionSource === 'function'
    ? Boolean(permissionSource(ORG_MANAGE_PLANS_PERMISSIONS))
    : hasAnyPermissionIn(ORG_MANAGE_PLANS_PERMISSIONS, permissionSource);
  if (granted) return true;
  const permissionMap = typeof permissionSource === 'function' ? null : permissionSource;
  if (hasPopulatedPermissionMap(permissionMap, user)) return false;
  return LEGACY_PLAN_REQUEST_ROLES.includes(Number(user?.role?.staffRoleId));
}

export function orgDetailPathFromRequest(row) {
  const socialNo = row?.org_social_no || row?.social_no;
  if (row?.hash) return `/corporations/users/${row.hash}`;
  if (!socialNo) return null;
  try {
    if (typeof btoa === 'function') {
      return `/corporations/users/${btoa(String(socialNo))}`;
    }
  } catch (_) {
    /* fall through */
  }
  return null;
}

export function formatPlanRequestCost(value) {
  if (value == null || value === '') return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return num.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

export function planRequestStatusColor(status) {
  switch ((status || '').toLowerCase()) {
    case 'open':
    case 'pending':
      return 'processing';
    case 'countered':
      return 'warning';
    case 'org_accepted':
      return 'cyan';
    case 'approved':
      return 'success';
    case 'rejected':
      return 'error';
    case 'cancelled':
      return 'default';
    default:
      return 'default';
  }
}

export function planRequestActionColor(action) {
  return (action || '').toLowerCase() === 'remove' ? 'volcano' : 'green';
}

export function isPlanRequestOpen(row) {
  const status = String(row?.status || '').toLowerCase();
  if (isRemovePlanRequest(row) || row?.flow === 'removal_ticket') {
    return status === 'pending';
  }
  return PLAN_REQUEST_OPEN_STATUSES.has(status);
}

export function isAddPlanRequest(row) {
  if (!row) return false;
  if (row.flow === 'negotiation') return true;
  if (row.flow === 'removal_ticket') return false;
  return String(row.action || '').toLowerCase() === 'add';
}

export function isRemovePlanRequest(row) {
  if (!row) return false;
  if (row.flow === 'removal_ticket') return true;
  if (row.flow === 'negotiation') return false;
  return String(row.action || '').toLowerCase() === 'remove';
}

export function isNegotiationFlow(row) {
  return isAddPlanRequest(row);
}

/** Prefer API can_* flags; fall back for older backends. */
export function canApprovePlanRequest(row) {
  if (!row || !isPlanRequestOpen(row)) return false;
  if (typeof row.can_approve === 'boolean') return row.can_approve;
  if (isRemovePlanRequest(row)) return String(row.status || '').toLowerCase() === 'pending';
  return String(row.status || '').toLowerCase() === 'org_accepted';
}

export function canForceApprovePlanRequest(row) {
  if (!row || !isNegotiationFlow(row) || !isPlanRequestOpen(row)) return false;
  if (typeof row.can_force_approve === 'boolean') return row.can_force_approve;
  return true;
}

export function canCounterPlanRequest(row) {
  if (!row || !isNegotiationFlow(row) || !isPlanRequestOpen(row)) return false;
  if (typeof row.can_counter === 'boolean') return row.can_counter;
  return true;
}

export function canRejectPlanRequest(row) {
  if (!row || !isPlanRequestOpen(row)) return false;
  if (typeof row.can_reject === 'boolean') return row.can_reject;
  return true;
}

export function formatWaitingOn(waitingOn) {
  const v = String(waitingOn || '').toLowerCase();
  if (!v) return null;
  if (v === 'admin' || v === 'care_admin' || v === 'account') return 'Waiting on account team';
  if (v === 'org' || v === 'hr' || v === 'organization') return 'Waiting on organization';
  return `Waiting on ${waitingOn}`;
}

export function extractApiErrorCode(parsed) {
  if (!parsed) return null;
  return parsed.code
    || parsed?.data?.code
    || parsed?.body?.code
    || parsed?.body?.data?.code
    || null;
}

export function isPlanRemovalBlocked(parsed) {
  if (!parsed) return false;
  return parsed.httpStatus === 409
    || extractApiErrorCode(parsed) === 'PLAN_REMOVAL_BLOCKED'
    || (Array.isArray(parsed.blockingPolicies) && parsed.blockingPolicies.length > 0);
}

export function isAcceptRequired(parsed) {
  if (!parsed) return false;
  return extractApiErrorCode(parsed) === 'ACCEPT_REQUIRED'
    || (parsed.httpStatus === 409 && /accept/i.test(String(parsed.message || '')));
}

export function supportsIndividualTier(row) {
  if (row?.supports_individual === false || row?.supports_individual === 0) return false;
  return true;
}

export function supportsFamilyTier(row) {
  if (row?.supports_family === false || row?.supports_family === 0) return false;
  return true;
}

const pickFirst = (...vals) => {
  for (const v of vals) {
    if (v != null && v !== '') return v;
  }
  return null;
};

/** Expected policy count for a tier (API field is still `tentative_heads`). */
export function pickExpectedPolicies(...sources) {
  return pickFirst(
    ...sources.flatMap((src) => {
      if (src == null || typeof src !== 'object') return [src];
      return [
        src.tentative_heads,
        src.expected_policies,
        src.expected_number_of_policies,
        src.expected_policies_count,
        src.policies,
      ];
    }),
  );
}

/** Latest negotiated terms from negotiation snapshot, latest offer, or flat row fields. */
export function getNegotiationTerms(row) {
  const neg = row?.negotiation && typeof row.negotiation === 'object' ? row.negotiation : {};
  const individual = neg.individual || {};
  const family = neg.family || {};

  const offers = getPlanRequestOffers(row);
  const latestOffer = offers.length
    ? [...offers].sort((a, b) => (Number(a.round) || 0) - (Number(b.round) || 0)).at(-1)
    : null;

  return {
    individual_heads: pickExpectedPolicies(
      individual,
      latestOffer?.individual,
      row?.individual_heads,
      row?.tentative_individual_heads,
      row?.requested_individual_heads,
      row?.expected_individual_policies,
      neg.individual_heads,
      latestOffer?.individual_heads,
      latestOffer?.tentative_individual_heads,
    ),
    individual_unit: pickFirst(
      individual.requested_unit_cost,
      individual.confirmed_unit_cost,
      latestOffer?.individual?.requested_unit_cost,
      latestOffer?.individual?.confirmed_unit_cost,
      row?.org_plan_cost,
      latestOffer?.org_plan_cost,
      latestOffer?.requested_unit_cost,
    ),
    family_heads: pickExpectedPolicies(
      family,
      latestOffer?.family,
      row?.family_heads,
      row?.tentative_family_heads,
      row?.requested_family_heads,
      row?.expected_family_policies,
      neg.family_heads,
      latestOffer?.family_heads,
      latestOffer?.tentative_family_heads,
    ),
    family_unit: pickFirst(
      family.requested_unit_cost,
      family.confirmed_unit_cost,
      latestOffer?.family?.requested_unit_cost,
      latestOffer?.family?.confirmed_unit_cost,
      row?.org_plan_family_cost,
      latestOffer?.org_plan_family_cost,
      latestOffer?.requested_family_cost,
    ),
  };
}

export function getPlanRequestOffers(row) {
  if (!row || isRemovePlanRequest(row)) return [];
  if (Array.isArray(row.offers)) return row.offers;
  if (Array.isArray(row.negotiation?.offers)) return row.negotiation.offers;
  return [];
}

export function formatOfferActor(offer) {
  const actor = offer?.actor_type || offer?.actor || offer?.created_by_type || offer?.by || offer?.side;
  const v = String(actor || '').toLowerCase();
  if (v === 'admin' || v === 'care_admin' || v === 'staff') return 'Account team';
  if (v === 'org' || v === 'hr' || v === 'organization') return 'Organization';
  return actor || '—';
}

export function offerUnitCost(offer) {
  return offer?.individual?.requested_unit_cost
    ?? offer?.individual?.confirmed_unit_cost
    ?? offer?.org_plan_cost
    ?? offer?.requested_unit_cost
    ?? null;
}

export function offerFamilyCost(offer) {
  return offer?.family?.requested_unit_cost
    ?? offer?.family?.confirmed_unit_cost
    ?? offer?.org_plan_family_cost
    ?? offer?.requested_family_cost
    ?? null;
}

export function offerIndividualHeads(offer) {
  return pickExpectedPolicies(offer?.individual, offer?.individual_heads, offer?.tentative_individual_heads);
}

export function offerFamilyHeads(offer) {
  return pickExpectedPolicies(offer?.family, offer?.family_heads, offer?.tentative_family_heads);
}

/** Build POST /offers body from form values + tier support flags. */
export function buildAdminOfferPayload(values, row) {
  const note = values?.note || undefined;
  const payload = { note };
  const indCost = values?.org_plan_cost;
  const famCost = values?.org_plan_family_cost;
  const indHeads = values?.individual_heads;
  const famHeads = values?.family_heads;

  if (supportsIndividualTier(row)) {
    const individual = {};
    if (indCost != null && indCost !== '') individual.requested_unit_cost = Number(indCost);
    if (indHeads != null && indHeads !== '') individual.tentative_heads = Number(indHeads);
    if (Object.keys(individual).length) payload.individual = individual;
    if (indCost != null && indCost !== '') payload.org_plan_cost = Number(indCost);
  }
  if (supportsFamilyTier(row)) {
    const family = {};
    if (famCost != null && famCost !== '') family.requested_unit_cost = Number(famCost);
    if (famHeads != null && famHeads !== '') family.tentative_heads = Number(famHeads);
    if (Object.keys(family).length) payload.family = family;
    if (famCost != null && famCost !== '') payload.org_plan_family_cost = Number(famCost);
  }
  return payload;
}
